import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useUiStore } from '../../store/uiStore';
import { useLanguageStore } from '../../store/languageStore';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { Menu, LogOut, Bell, Search, User } from 'lucide-react';

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { toggleSidebar } = useUiStore();
  const { isRTL } = useLanguageStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="navbar navbar-expand-lg navbar-light bg-white border-bottom shadow-sm sticky-top"
      style={{ 
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        zIndex: 1030,
        minHeight: '64px',
        width: '100%',
        maxWidth: '100%'
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container-fluid px-4" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <div className="d-flex align-items-center justify-content-between w-100">
          {isRTL ? (
            <>
              {/* Right Side - Search and User Info (RTL) */}
              <div className="d-flex align-items-center gap-2">
                {/* Search Bar - Desktop - First in RTL */}
                <motion.div
                  className="input-group d-none d-md-flex"
                  style={{ width: '300px', maxWidth: '100%' }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <input
                    type="text"
                    className="form-control border-0 bg-light"
                    placeholder={t('common.search')}
                    dir="rtl"
                  />
                  <span className="input-group-text bg-light border-0">
                    <Search size={18} className="text-muted" />
                  </span>
                </motion.div>

                {/* User Info */}
                {user && (
                  <motion.div
                    className="d-none d-sm-flex align-items-center gap-2 px-2 py-1 rounded flex-row-reverse"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div 
                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-semibold shadow-sm"
                      style={{ width: '36px', height: '36px', fontSize: '14px' }}
                    >
                      <User size={18} />
                    </div>
                    <div className="d-flex flex-column text-end">
                      <span className="small fw-semibold text-dark">{user.username}</span>
                      <span className="small text-muted">{t('common.adminPanel')}</span>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Left Side - Language, Notifications, and Logout (RTL) */}
              <div className="d-flex align-items-center gap-2">
                {/* Mobile Menu Button */}
                <motion.button
                  onClick={toggleSidebar}
                  className="btn btn-link d-lg-none text-dark p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Toggle sidebar"
                >
                  <Menu size={24} />
                </motion.button>

                {/* Language Switcher */}
                <LanguageSwitcher />

                {/* Notifications - Between Language and Logout */}
                <motion.button
                  className="btn btn-link position-relative text-dark p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                  <motion.span
                    className="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: '8px', width: '8px', height: '8px', padding: 0 }}
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.button>

                {/* Logout Button */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button
                    variant="text"
                    size="sm"
                    onClick={handleLogout}
                    className="text-danger"
                  >
                    {isRTL ? (
                      <>
                        <LogOut className="ms-2" size={16} />
                        <span className="d-none d-sm-inline">{t('common.logout')}</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="me-2" size={16} />
                        <span className="d-none d-sm-inline">{t('common.logout')}</span>
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </>
          ) : (
            <>
              {/* Left Side - Search (LTR) */}
              <div className="d-flex align-items-center gap-2">
                {/* Mobile Menu Button */}
                <motion.button
                  onClick={toggleSidebar}
                  className="btn btn-link d-lg-none text-dark p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Toggle sidebar"
                >
                  <Menu size={24} />
                </motion.button>

                {/* Search Bar - Desktop */}
                <motion.div
                  className="input-group d-none d-md-flex"
                  style={{ width: '300px', maxWidth: '100%' }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="input-group-text bg-light border-0">
                    <Search size={18} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-0 bg-light"
                    placeholder={t('common.search')}
                    dir="ltr"
                  />
                </motion.div>
              </div>

              {/* Right Side - Actions (LTR) */}
              <div className="d-flex align-items-center gap-2">
                {/* Language Switcher */}
                <LanguageSwitcher />

                {/* Notifications */}
                <motion.button
                  className="btn btn-link position-relative text-dark p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                  <motion.span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: '8px', width: '8px', height: '8px', padding: 0 }}
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.button>

                {/* User Info */}
                {user && (
                  <motion.div
                    className="d-none d-sm-flex align-items-center gap-2 px-2 py-1 rounded"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div 
                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-semibold shadow-sm"
                      style={{ width: '36px', height: '36px', fontSize: '14px' }}
                    >
                      <User size={18} />
                    </div>
                    <div className="d-flex flex-column text-start">
                      <span className="small fw-semibold text-dark">{user.username}</span>
                      <span className="small text-muted">{t('common.adminPanel')}</span>
                    </div>
                  </motion.div>
                )}

                {/* Logout Button */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button
                    variant="text"
                    size="sm"
                    onClick={handleLogout}
                    className="text-danger"
                  >
                    {isRTL ? (
                      <>
                        <LogOut className="ms-2" size={16} />
                        <span className="d-none d-sm-inline">{t('common.logout')}</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="me-2" size={16} />
                        <span className="d-none d-sm-inline">{t('common.logout')}</span>
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.nav>
  );
};
